import * as React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { StripeEmbeddedCheckout } from "../components/commerce";
import BookDescription from "../components/dashboard/BookDescription";
import { BookCover } from "../components/common";
import { appContext } from "../hooks/provider";
import tierDefinitions from "../data/tierDefinitions.json";

/**
 * A URL that means pay.
 *
 * The first version of this page was a pricing table: you landed on
 * /checkout/professional and still had to click "Get Professional" to reach
 * Stripe. That is a link meaning "look at prices again", not "buy". The
 * embedded checkout is now mounted immediately for whichever tier the URL
 * names, so the only remaining action is paying.
 *
 * The tier is a path segment, so each edition is its own linkable URL and
 * switching updates the address — a shared link and analytics both stay
 * truthful. ?tier= is accepted for hand-written links and redirects here.
 *
 * The URL only chooses which sku to open. payment.routes.ts resolves the price
 * server-side via getTierDefinition(sku), so nothing here can change what
 * anyone is charged; an unknown value falls back rather than 404s.
 *
 * No marketing on this page. It is noindexed and reached by people who have
 * already decided; multiagentbook.com carries the case for the book.
 */

type TierId = "essential" | "professional" | "enterprise";

interface Tier {
  id: string;
  sku: string;
  name: string;
  priceDisplay: string;
  description: string;
  displayFeatures?: string[];
}

const TIERS = Object.values(tierDefinitions as Record<string, Tier>);
const VALID = TIERS.map((t) => t.id);
const DEFAULT_TIER: TierId = "essential";

const parseTier = (raw?: string | null): TierId | null => {
  if (!raw) return null;
  const t = raw.toLowerCase().trim();
  return VALID.includes(t) ? (t as TierId) : null;
};

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation("home");
  const { tier: tierParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryTier = parseTier(searchParams.get("tier"));
  const pathTier = parseTier(tierParam);

  if (!pathTier && queryTier) return <Navigate to={`/checkout/${queryTier}`} replace />;

  const active: TierId = pathTier || DEFAULT_TIER;
  const activeTier = TIERS.find((t) => t.id === active)!;

  return (
    <Layout title="Checkout" link="/checkout" description="Complete your purchase of Designing Multi-Agent Systems.">
      <appContext.Consumer>
        {({ user }: any) => (
          <div className="py-8">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              {/* What you are buying. Stripe's form has a hard ~560px floor, so
                  the page is wide whether we use it or not — this column is the
                  space that would otherwise be empty, spent on the one question
                  a checkout has to answer: what do I get for this. Reassurance
                  about the purchase, not a sales pitch: no testimonials, no
                  "why this book". Title, subtitle and description are the same
                  i18n strings and BookDescription block the home page renders. */}
              <div className="flex-1 min-w-0 w-full order-2 lg:order-1">
                {/* The cover floats so the title, subtitle and description all
                    wrap around it. As a flex row it only sat beside the title,
                    leaving a column of dead space under it while the text
                    started below the whole image. */}
                <div className="float-left mr-7 mb-4 w-44 md:w-56">
                  <BookCover size="mobile" width="w-full" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary leading-tight">
                  {t("hero.title")}
                </h1>
                <p className="text-lg text-secondary mt-1">{t("hero.subtitle")}</p>
                <BookDescription className="mt-4" showSubtitle={false} />

                {/* The tier switcher lives with the description, because
                    choosing an edition is part of deciding what you get — the
                    payment column stays a payment column. */}
                <div className="mt-8 clear-both">
                  <h2 className="text-sm font-semibold text-primary mb-3">Choose your edition</h2>
                  <div
                    className="grid gap-3"
                    style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}
                  >
                    {TIERS.map((tier) => {
                      const selected = tier.id === active;
                      return (
                        <button
                          key={tier.id}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => navigate(`/checkout/${tier.id}`)}
                          className={`text-left rounded-lg border p-4 transition-colors h-full ${
                            selected
                              ? "border-accent ring-1 ring-accent/40 bg-accent/5"
                              : "border-secondary/50 hover:border-accent/40"
                          }`}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-semibold text-primary">{tier.name}</span>
                            <span className="text-lg font-bold text-primary">{tier.priceDisplay}</span>
                          </div>
                          <p className="text-xs text-secondary mt-1">{tier.description}</p>
                          {tier.displayFeatures && (
                            <ul className="mt-3 space-y-1">
                              {tier.displayFeatures.map((f) => (
                                <li key={f} className="text-xs text-secondary flex gap-1.5">
                                  <span className={selected ? "text-accent" : "text-secondary/60"}>✓</span>
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-xs text-secondary mt-4">
                    Prefer print?{" "}
                    <a
                      className="text-accent hover:underline"
                      href="https://www.amazon.com/dp/B0G2BCQQJY"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Paperback and hardcover on Amazon
                    </a>
                  </p>
                </div>
              </div>

              {/* Payment. First in the DOM order on a phone, where a wall of
                  text above the form is exactly what this page was fixing. */}
              <div className="w-full lg:w-[600px] flex-shrink-0 order-1 lg:order-2">
                <div className="border border-secondary/50 rounded-lg overflow-hidden bg-white">
                  {/* key forces a fresh session when the sku changes — Stripe
                      caches the client secret per provider instance */}
                  <StripeEmbeddedCheckout
                    key={activeTier.sku}
                    sku={activeTier.sku}
                    userEmail={user?.email || ""}
                    padded={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </appContext.Consumer>
    </Layout>
  );
};

export default CheckoutPage;
